import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Award, BookOpen, Check, CheckCircle2, ChevronLeft, ChevronRight, CircleHelp,
  Download, FileText, Link2, Loader2, Lock, Menu, PlayCircle, StickyNote, X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RichTextBlocks } from "@/components/RichText";
import { LessonVideo, downloadProtectedFile } from "@/components/ProtectedMedia";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function LessonRow({ lesson, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`nav-lesson-${lesson.id}`}
      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left transition-colors ${
        active ? "bg-brand-light" : "hover:bg-slate-50"
      }`}
    >
      {lesson.completed ? (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
      ) : active ? (
        <PlayCircle className="h-4 w-4 shrink-0 text-brand" />
      ) : (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-slate-300" />
      )}
      <span className={`flex-1 text-[13px] leading-snug ${active ? "font-bold text-brand" : "font-medium text-slate-600"}`}>
        {lesson.title}
      </span>
      <span className="text-[11px] text-slate-400">{lesson.duration}</span>
    </button>
  );
}

function ProgramList({ space, currentId, onSelect }) {
  return (
    <div className="space-y-3">
      {space.program.map((m) => (
        <div key={m.id} className="rounded-xl border border-line">
          <div className="flex items-center justify-between px-3 py-2.5">
            <p className="text-[13px] font-bold text-ink">{m.title}</p>
            <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
              {m.completed_count}/{m.total_count}
            </span>
          </div>
          <div className="border-t border-line p-1.5">
            {m.lessons.map((l) => (
              <LessonRow key={l.id} lesson={l} active={l.id === currentId} onClick={() => onSelect(l.id)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const FILE_ICON = { pdf: FileText, zip: Download, docx: FileText, xlsx: FileText };

export default function FormationLearn() {
  const { formationId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [space, setSpace] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | denied | notfound
  const [lessonLoading, setLessonLoading] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [notes, setNotes] = useState("");

  const loadSpace = async () => {
    try {
      const { data } = await api.get(`/my/formations/${formationId}`);
      setSpace(data);
      setStatus("ok");
      return data;
    } catch (e) {
      setStatus(e?.response?.status === 403 ? "denied" : "notfound");
      return null;
    }
  };

  useEffect(() => {
    setStatus("loading");
    loadSpace().then((data) => {
      if (data && !lessonId && data.last_lesson_id) {
        navigate(`/mes-formations/${formationId}/${data.last_lesson_id}`, { replace: true });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formationId]);

  useEffect(() => {
    if (!lessonId) return;
    setLessonLoading(true);
    api.get(`/my/formations/${formationId}/lessons/${lessonId}`)
      .then(({ data }) => setLesson(data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLessonLoading(false));
    setNotes(localStorage.getItem(`ismo_note_${lessonId}`) || "");
    setDrawer(false);
    window.scrollTo({ top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, formationId]);

  const selectLesson = (id) => navigate(`/mes-formations/${formationId}/${id}`);

  const markComplete = async () => {
    try {
      const { data } = await api.post(`/my/formations/${formationId}/lessons/${lessonId}/complete`);
      setLesson((l) => ({ ...l, completed: data.completed }));
      const fresh = await loadSpace();
      if (data.finished) {
        toast.success("🎉 Formation terminée !", { description: data.certificate ? "Votre certificat est disponible." : "" });
      } else {
        toast.success(data.completed ? "Cours marqué comme terminé" : "Cours marqué comme non terminé");
      }
      // auto-advance
      if (data.completed && lesson?.next_id) selectLesson(lesson.next_id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveNote = (v) => {
    setNotes(v);
    localStorage.setItem(`ismo_note_${lessonId}`, v);
  };

  if (status === "loading") {
    return <div className="flex min-h-screen items-center justify-center bg-surface"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;
  }
  if (status === "denied") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface px-6 text-center" data-testid="access-denied">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50"><Lock className="h-8 w-8 text-rose-500" /></span>
        <h1 className="font-display text-2xl font-extrabold text-ink">Vous n'avez pas accès à cette formation</h1>
        <p className="max-w-md text-sm text-slate-500">Achetez cette formation pour accéder à son contenu pédagogique complet.</p>
        <div className="flex gap-3">
          <Link to="/formations" className="rounded-full border border-line px-5 py-2.5 text-sm font-bold text-ink hover:border-brand">Voir les formations</Link>
          <Link to="/compte/formations" className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white">Mes formations</Link>
        </div>
      </div>
    );
  }
  if (status === "notfound" || !space) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-surface text-center">
        <p className="font-display text-2xl font-extrabold text-ink">Formation introuvable</p>
        <Link to="/compte/formations" className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white">Mes formations</Link>
      </div>
    );
  }

  const currentModule = space.program.find((m) => m.lessons.some((l) => l.id === lessonId));
  const lessonIndexLabel = (() => {
    if (!currentModule) return "";
    const mi = space.program.indexOf(currentModule) + 1;
    const li = currentModule.lessons.findIndex((l) => l.id === lessonId) + 1;
    return `${mi}.${li}`;
  })();

  const Sidebar = (
    <div className="flex h-full flex-col">
      <div className="relative overflow-hidden rounded-2xl bg-night p-4 text-white">
        <img src={space.image_url} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative">
          <p className="font-display text-sm font-bold leading-snug">{space.title}</p>
          <div className="mt-3 flex items-center justify-between text-[11px] font-semibold">
            <span>Progression</span><span>{space.progress}%</span>
          </div>
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-white/20">
            <div className="h-full rounded-full bg-brand" style={{ width: `${space.progress}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-slate-300">
            {space.completed_lessons.length} / {space.total_lessons} cours complétés
          </p>
        </div>
      </div>

      {space.finished && space.certificate_enabled && space.certificate?.certificate_number && (
        <Link
          to={`/certificat/${space.certificate.certificate_number}`}
          data-testid="download-certificate-btn"
          className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-amber-600"
        >
          <Award className="h-4 w-4" /> Voir mon certificat
        </Link>
      )}

      <div className="mt-4 flex-1 overflow-y-auto pr-1">
        <ProgramList space={space} currentId={lessonId} onSelect={selectLesson} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-surface">
      {/* TOP BAR */}
      <header className="sticky top-0 z-40 border-b border-line bg-white">
        <div className="flex h-16 items-center gap-3 px-3 sm:px-5">
          <button onClick={() => setDrawer(true)} data-testid="learn-menu-btn" className="flex h-9 w-9 items-center justify-center rounded-lg text-ink hover:bg-slate-100 lg:hidden">
            <Menu className="h-5 w-5" />
          </button>
          <Link to="/" className="hidden items-center gap-2 sm:flex">
            <span className="font-display text-lg font-extrabold text-ink">ISMO<span className="text-brand">-TECH</span></span>
          </Link>
          <div className="hidden h-6 w-px bg-line lg:block" />
          <div className="hidden min-w-0 flex-1 items-center gap-2 lg:flex">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-light text-brand"><BookOpen className="h-4 w-4" /></span>
            <p className="truncate text-sm font-bold text-ink">{space.title}</p>
          </div>
          <div className="mx-auto flex flex-1 items-center gap-3 lg:mx-0 lg:max-w-xs">
            <div className="hidden text-xs font-semibold text-slate-500 sm:block">Progression</div>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${space.progress}%` }} />
            </div>
            <span className="text-sm font-bold text-brand" data-testid="learn-progress">{space.progress}%</span>
          </div>
          <Link to="/compte/formations" data-testid="learn-back-btn" className="flex items-center gap-1.5 rounded-full border border-line px-3 py-2 text-xs font-bold text-ink hover:border-brand hover:text-brand">
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Retour</span>
          </Link>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1500px] gap-6 px-3 py-5 sm:px-5">
        {/* LEFT SIDEBAR */}
        <aside className="hidden w-[300px] shrink-0 lg:block">
          <div className="sticky top-[84px] h-[calc(100vh-104px)]">{Sidebar}</div>
        </aside>

        {/* MAIN */}
        <main className="min-w-0 flex-1">
          {lessonLoading || !lesson ? (
            <div className="flex h-96 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
          ) : (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 text-xs font-bold text-brand">
                    <PlayCircle className="h-4 w-4" /> Cours {lessonIndexLabel}
                  </p>
                  <h1 className="mt-1 font-display text-2xl font-extrabold text-ink sm:text-3xl">{lesson.title}</h1>
                  {lesson.description && <p className="mt-1.5 text-sm text-slate-500">{lesson.description}</p>}
                </div>
                <button
                  onClick={markComplete}
                  data-testid="mark-complete-btn"
                  className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-colors ${
                    lesson.completed ? "bg-emerald-50 text-emerald-700" : "border border-line text-ink hover:border-brand hover:text-brand"
                  }`}
                >
                  <CheckCircle2 className="h-4 w-4" /> {lesson.completed ? "Cours terminé" : "Marquer comme terminé"}
                </button>
              </div>

              {/* video */}
              {lesson.video ? (
                <LessonVideo video={lesson.video} poster={space.image_url} />
              ) : (
                <div className="flex aspect-video items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <div className="text-center"><FileText className="mx-auto h-10 w-10" /><p className="mt-2 text-sm">Cours écrit — voir le contenu ci-dessous</p></div>
                </div>
              )}

              {/* prev/next */}
              <div className="flex items-center justify-between gap-3">
                <button disabled={!lesson.prev_id} onClick={() => lesson.prev_id && selectLesson(lesson.prev_id)} data-testid="prev-lesson-btn" className="flex items-center gap-1.5 rounded-full border border-line px-4 py-2.5 text-sm font-bold text-ink transition-colors hover:border-brand disabled:cursor-not-allowed disabled:opacity-40">
                  <ChevronLeft className="h-4 w-4" /> Cours précédent
                </button>
                <button disabled={!lesson.next_id} onClick={() => lesson.next_id && selectLesson(lesson.next_id)} data-testid="next-lesson-btn" className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-40">
                  Cours suivant <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* tabs */}
              <Tabs defaultValue="apropos">
                <TabsList className="flex w-full flex-wrap justify-start gap-1 border-b border-line bg-transparent p-0">
                  <TabsTrigger value="apropos" className="rounded-none border-b-2 border-transparent px-3 pb-2 data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-brand">À propos de ce cours</TabsTrigger>
                  <TabsTrigger value="ressources" data-testid="tab-ressources" className="rounded-none border-b-2 border-transparent px-3 pb-2 data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-brand">Ressources</TabsTrigger>
                  <TabsTrigger value="notes" className="rounded-none border-b-2 border-transparent px-3 pb-2 data-[state=active]:border-brand data-[state=active]:bg-transparent data-[state=active]:text-brand">Notes</TabsTrigger>
                </TabsList>

                <TabsContent value="apropos" className="mt-4">
                  <div className="rounded-2xl border border-line bg-white p-5 text-sm leading-relaxed text-slate-600">
                    {lesson.text_content ? <RichTextBlocks text={lesson.text_content} /> : <p>Aucune description pour ce cours.</p>}
                    {lesson.links?.length > 0 && (
                      <div className="mt-4">
                        <p className="mb-2 font-bold text-ink">Liens utiles</p>
                        <ul className="space-y-1.5">
                          {lesson.links.map((l, i) => (
                            <li key={i}><a href={l.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand hover:underline"><Link2 className="h-4 w-4" /> {l.label || l.url}</a></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {lesson.notes && (
                      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
                        <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700"><StickyNote className="h-4 w-4" /> À retenir</p>
                        <p className="mt-1 text-sm text-amber-800">{lesson.notes}</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="ressources" className="mt-4">
                  <div className="rounded-2xl border border-line bg-white p-5">
                    <h3 className="mb-3 font-display text-sm font-bold text-ink">Ressources du cours</h3>
                    {lesson.files?.length ? (
                      <div className="space-y-2">
                        {lesson.files.map((file, i) => {
                          const Icon = FILE_ICON[file.ext] || FileText;
                          return (
                            <div key={i} className="flex items-center gap-3 rounded-xl border border-line p-3">
                              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-light text-brand"><Icon className="h-5 w-5" /></span>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-bold text-ink">{file.name}</p>
                                <p className="text-xs uppercase text-slate-400">{file.ext}{file.size ? ` • ${(file.size / 1048576).toFixed(1)} Mo` : ""}</p>
                              </div>
                              <button onClick={() => downloadProtectedFile(file)} data-testid={`resource-download-${i}`} className="flex h-9 w-9 items-center justify-center rounded-lg text-brand hover:bg-brand-soft"><Download className="h-4 w-4" /></button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Aucune ressource pour ce cours.</p>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="mt-4">
                  <div className="rounded-2xl border border-line bg-white p-5">
                    <h3 className="mb-2 font-display text-sm font-bold text-ink">Mes notes personnelles</h3>
                    <textarea
                      value={notes}
                      onChange={(e) => saveNote(e.target.value)}
                      data-testid="personal-notes"
                      placeholder="Prenez des notes sur ce cours... (sauvegardées automatiquement sur cet appareil)"
                      className="min-h-[160px] w-full rounded-xl border border-line p-3 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15"
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </main>

        {/* RIGHT SIDEBAR */}
        <aside className="hidden w-[300px] shrink-0 xl:block">
          <div className="sticky top-[84px] space-y-4">
            {lesson?.files?.length > 0 && (
              <div className="rounded-2xl border border-line bg-white p-4">
                <h3 className="mb-3 font-display text-sm font-bold text-ink">Ressources du cours</h3>
                <div className="space-y-2">
                  {lesson.files.map((file, i) => {
                    const Icon = FILE_ICON[file.ext] || FileText;
                    return (
                      <button key={i} onClick={() => downloadProtectedFile(file)} className="flex w-full items-center gap-2.5 rounded-xl border border-line p-2.5 text-left hover:border-brand">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-light text-brand"><Icon className="h-4 w-4" /></span>
                        <span className="min-w-0 flex-1"><span className="block truncate text-xs font-bold text-ink">{file.name}</span><span className="text-[10px] uppercase text-slate-400">{file.ext}</span></span>
                        <Download className="h-4 w-4 text-slate-400" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="rounded-2xl border border-line bg-white p-4">
              <p className="flex items-center gap-2 font-display text-sm font-bold text-ink"><CircleHelp className="h-4 w-4 text-brand" /> Besoin d'aide ?</p>
              <p className="mt-1 text-xs text-slate-500">Notre équipe est là pour vous accompagner.</p>
              <Link to="/support" className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-brand-soft px-4 py-2.5 text-xs font-bold text-brand hover:bg-brand-light">Contacter le support</Link>
            </div>
          </div>
        </aside>
      </div>

      {/* MOBILE PROGRAM DRAWER */}
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="w-[88%] max-w-sm overflow-y-auto p-4">
          <SheetTitle className="sr-only">Programme de la formation</SheetTitle>
          <div className="mb-3 flex items-center justify-between">
            <p className="font-display text-base font-bold text-ink">Programme</p>
            <button onClick={() => setDrawer(false)} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
          </div>
          {Sidebar}
        </SheetContent>
      </Sheet>
    </div>
  );
}
